import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import {
  Box,
  useTheme,
  IconButton,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import LeftSidebar from "../../components/swagstore/LeftSidebar";
import RightSidebar from "../../components/swagstore/RightSidebar";
import StorePreview from "../../components/swagstore/StorePreview";
import SwagstoreHeader from "../../components/swagstore/SwagstoreHeader";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { SwagstoreRequestBody } from "../../../models/Swagstore/SwagstoreRequestBody";
import { ThemeRequestBody } from "../../../models/Swagstore/ThemeRequestBody";
import { createEmptyBranding } from "../../../models/Swagstore/BrandingModel";
import { ProductMappingRequestBody } from "../../../models/Swagstore/ProductMappingRequestBody";
import ImageAPI from "../../../api/ImageAPI";
import { SelectedItem } from "../../../stores/SwagStoreStore";
import {
  SWAGSTORE_STEPS,
  SWAGSTORE_STEP_VALUES,
} from "../../../constants/SwagstoreConstants";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";

const Swagstore = observer(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { swagStoreStore, customerStore, userStore } = useStores();
  const { showSuccess, showError } = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Initialize selected item from URL on mount
  useEffect(() => {
    const step = searchParams.get("step");
    if (step && SWAGSTORE_STEP_VALUES.includes(step)) {
      swagStoreStore.setSelectedItem(step as SelectedItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const storeIdFromUrl = searchParams.get("store_id");
    const themeIdFromUrl = searchParams.get("theme_id");

    if (!storeIdFromUrl && !themeIdFromUrl) {
      if (swagStoreStore.storeId !== null || swagStoreStore.themeId !== null) {
        swagStoreStore.resetStore();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchBothPacksAndProducts = () => {
    if (
      (swagStoreStore.selectedItem === SWAGSTORE_STEPS.PRODUCT_MAPPING ||
        swagStoreStore.selectedItem === SWAGSTORE_STEPS.MOCKUPS) &&
      swagStoreStore.storeId &&
      swagStoreStore.settings.storeDetails.customer_id &&
      swagStoreStore.productsToDisplayOnProductPage.length === 0
    ) {
      Promise.all([swagStoreStore.getPacks(), swagStoreStore.getProducts()])
        .catch(console.error)
        .finally(() => {
          swagStoreStore.setProductsToDisplayOnProductPage();
          swagStoreStore.setBaseProductsToDisplay();
        });
    }
  };

  useEffect(() => {
    if (
      swagStoreStore.selectedItem === SWAGSTORE_STEPS.PRODUCT_MAPPING &&
      swagStoreStore.categories.length === 0
    ) {
      swagStoreStore.getCategories().catch((err) => {
        console.error("Error fetching categories:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swagStoreStore.selectedItem]);

  useEffect(() => {
    if (
      swagStoreStore.selectedItem === SWAGSTORE_STEPS.STORE_DETAILS &&
      swagStoreStore.authenticationMethods.length === 0 &&
      !swagStoreStore.loadingAuthenticationMethods
    ) {
      swagStoreStore.fetchAuthenticationMethods().catch((err) => {
        console.error("Error fetching authentication methods:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swagStoreStore.selectedItem]);

  useEffect(() => {
    fetchBothPacksAndProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    swagStoreStore.selectedItem,
    swagStoreStore.storeId,
    swagStoreStore.settings.storeDetails.customer_id,
  ]);

  // Update URL when selected item changes
  useEffect(() => {
    const currentStep = searchParams.get("step");
    if (swagStoreStore.selectedItem !== currentStep) {
      const params = new URLSearchParams(searchParams);
      if (swagStoreStore.selectedItem === SWAGSTORE_STEPS.STORE_DETAILS) {
        params.delete("step");
      } else {
        params.set("step", swagStoreStore.selectedItem);
      }

      if (!params.has("store_id") && swagStoreStore.storeId) {
        params.set("store_id", swagStoreStore.storeId.toString());
      }
      if (!params.has("theme_id") && swagStoreStore.themeId) {
        params.set("theme_id", swagStoreStore.themeId.toString());
      }
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swagStoreStore.selectedItem, navigate, searchParams]);

  useEffect(() => {
    const storeIdFromUrl = searchParams.get("store_id");
    const themeIdFromUrl = searchParams.get("theme_id");

    let shouldFetchDetails = false;
    let urlStoreId: string | null = null;
    let urlThemeId: number | null = null;

    if (storeIdFromUrl) {
      urlStoreId = storeIdFromUrl;
      if (swagStoreStore.storeId !== urlStoreId) {
        // Clear settings before loading new store to prevent showing old data
        swagStoreStore.clearSettings();
        swagStoreStore.setStoreId(urlStoreId);
        shouldFetchDetails = true;
      }
    }

    if (themeIdFromUrl) {
      urlThemeId = Number(themeIdFromUrl);
      if (swagStoreStore.themeId !== urlThemeId) {
        swagStoreStore.setThemeId(urlThemeId);
        shouldFetchDetails = true;
      }
    }

    if (shouldFetchDetails && urlStoreId && urlThemeId) {
      swagStoreStore.getStoreDetails().catch((error) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to fetch store details. Please try again.",
        );
        setTimeout(() => {
          navigate("/swagstore/listing");
        }, 1000);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const storeIdFromUrl = searchParams.get("store_id");
    const themeIdFromUrl = searchParams.get("theme_id");

    if (
      swagStoreStore.storeId &&
      swagStoreStore.themeId &&
      !storeIdFromUrl &&
      !themeIdFromUrl
    ) {
      swagStoreStore.getStoreDetails().catch((error) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to fetch store details. Please try again.",
        );
        // Redirect to listing page after showing error
        setTimeout(() => {
          navigate("/swagstore/listing");
        }, 1000);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!swagStoreStore.modalOpen) {
      return;
    }

    if (
      !swagStoreStore.storeId ||
      !swagStoreStore.settings.storeDetails.customer_id
    ) {
      return;
    }

    const isInventory = swagStoreStore.selectedSource === "inventory";

    const hasExistingData = isInventory
      ? swagStoreStore.fetchedInventoryProducts.length > 0
      : swagStoreStore.fetchedCatalogueProducts.length > 0;

    // Only fetch on initial load when modal opens or source changes
    // Don't fetch when search term changes - API should only be called on Enter key press
    if (hasExistingData) {
      return;
    }

    const fetchData = () => {
      if (isInventory) {
        if (!swagStoreStore.loadingInventory) {
          swagStoreStore.setInventoryPage(1);
          swagStoreStore.getPacks().catch(console.error);
        }
      } else {
        if (!swagStoreStore.loadingCatalogue) {
          swagStoreStore.setCataloguePage(1);
          swagStoreStore.getProducts().catch(console.error);
        }
      }
    };

    // Initial load - fetch immediately
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    swagStoreStore.modalOpen,
    swagStoreStore.selectedSource,
    // Removed inventorySearchTerm and catalogueSearchTerm from dependencies
    // API should only be called on Enter key press, not on every keystroke
    swagStoreStore.storeId,
    swagStoreStore.settings.storeDetails.customer_id,
  ]);

  const getPreviewWidth = (): string => {
    switch (swagStoreStore.deviceView) {
      case "mobile":
        return "375px";
      case "tablet":
        return "768px";
      default:
        return "100%";
    }
  };

  const getLoginPageImageUrl = (): string => {
    const loginImage = swagStoreStore.settings.loginPage.loginPageImage;
    if (loginImage) {
      if (typeof loginImage === "string") {
        return loginImage;
      } else if (loginImage instanceof File) {
        return URL.createObjectURL(loginImage);
      }
    }
    return "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/products/loginpage_1754639867409.png";
  };

  const handleItemSelect = (itemId: string) => {
    // Prevent selecting disabled items (all except STORE_DETAILS when storeId is missing)
    if (itemId !== SWAGSTORE_STEPS.STORE_DETAILS && !swagStoreStore.storeId) {
      return;
    }
    swagStoreStore.setSelectedItem(itemId as any);
    if (isMobile) {
      swagStoreStore.setLeftDrawerOpen(false);
    }
  };

  const handleThemeCHange = (step: string, key: string, value: any) => {
    switch (step) {
      case SWAGSTORE_STEPS.STORE_DETAILS:
        swagStoreStore.clearStoreDetailsError(key);
        swagStoreStore.updateStoreDetails({ [key]: value });
        break;
      case SWAGSTORE_STEPS.BRANDING:
        swagStoreStore.updateBranding({ [key]: value });
        break;
      case SWAGSTORE_STEPS.LOGIN:
        swagStoreStore.updateLoginPage({ [key]: value });
        break;
      case SWAGSTORE_STEPS.PRODUCT_MAPPING:
        if (key === "updateProduct") {
          // value should be { id, price }
          if (!swagStoreStore.storeId) {
            showError(
              "Error",
              "Store ID is required. Please create a store first.",
            );
            return;
          }

          const { id, price } = value;
          if (!id || price === undefined) {
            showError("Error", "Product ID and price are required.");
            return;
          }

          // Determine if it's a pack or product by checking if it exists in fetchedInventoryProducts (packs have inventory_id)
          const isPack = swagStoreStore.fetchedInventoryProducts.some(
            (item) => item.id === id,
          );

          const apiCall = isPack
            ? swagStoreStore.swagStoreAPI.updatePackPrice(
                swagStoreStore.storeId,
                id,
                price,
              )
            : swagStoreStore.swagStoreAPI.updateProductPrice(
                swagStoreStore.storeId,
                id,
                price,
              );

          apiCall
            .then((response) => {
              if (response) {
                showSuccess(
                  response.message ||
                    `${isPack ? "Pack" : "Product"} price updated successfully`,
                );
                // Refresh products and packs to get updated prices
                Promise.all([
                  swagStoreStore.getPacks(),
                  swagStoreStore.getProducts(),
                ])
                  .then(() => {
                    swagStoreStore.setProductsToDisplayOnProductPage();
                    swagStoreStore.setBaseProductsToDisplay(); // Update base products after price update
                  })
                  .catch((err) => {
                    console.error(
                      "Error refreshing products after price update:",
                      err,
                    );
                  });
              }
            })
            .catch((error: any) => {
              showError(
                "Error",
                error?.response?.data?.message ||
                  `Failed to update ${
                    isPack ? "pack" : "product"
                  } price. Please try again.`,
              );
            });
        } else {
          swagStoreStore.updateProductMapping({ [key]: value });
        }
        break;
      case SWAGSTORE_STEPS.MOCKUPS:
        break;
    }
  };

  const handleSaveSettings = async () => {
    const { selectedItem } = swagStoreStore;
    const { storeDetails, branding, loginPage, productMapping } =
      swagStoreStore.settings;

    try {
      switch (selectedItem) {
        case SWAGSTORE_STEPS.STORE_DETAILS: {
          const errors = swagStoreStore.validateStoreDetails();
          if (Object.keys(errors).length > 0) {
            swagStoreStore.setStoreDetailsErrors(errors);
            return;
          }

          swagStoreStore.setStoreDetailsErrors({});
          const requestBody: SwagstoreRequestBody = {
            store_name: storeDetails.store_name,
            customer_id: storeDetails.customer_id,
            is_public: storeDetails.is_public,
            domains: storeDetails.domains
              ? storeDetails.domains
                  .map((domain) => domain.domain)
                  .filter((domain) => domain && domain.trim() !== "")
              : [],
            authentication_method_ids:
              swagStoreStore.selectedAuthenticationMethods || [],
          };

          swagStoreStore
            .createSwagStore(requestBody)
            .then((res) => {
              showSuccess(res?.message || "Store created successfully");
              const params = new URLSearchParams(searchParams);
              params.set("store_id", swagStoreStore.storeId?.toString() || "");
              params.set("theme_id", swagStoreStore.themeId?.toString() || "");
              params.set("step", SWAGSTORE_STEPS.BRANDING);
              navigate({ search: params.toString() }, { replace: true });
              swagStoreStore.setSelectedItem(SWAGSTORE_STEPS.BRANDING);

              // Save theme details with default data in the background
              const defaultBranding = createEmptyBranding();
              const defaultThemeData: Partial<ThemeRequestBody> = {
                store_id: swagStoreStore.storeId!,
                theme_id: swagStoreStore.themeId!,
                app_button_color: defaultBranding.primaryColor,
                app_hover_button_color: defaultBranding.secondaryColor,
                app_button_secondary_color: defaultBranding.secondaryColor,
                app_button_secondary_hover_color:
                  defaultBranding.secondaryColor,
                store_background_color:
                  defaultBranding.storeBackgroundColor || "",
                active_textfield_color:
                  defaultBranding.textFieldActiveColor || "",
                disabled_textfield_color:
                  defaultBranding.textFieldDisabledColor || "",
                homepage_product_card_background:
                  defaultBranding.homepageProductCardBackground || "",
                homepage_product_name_background:
                  defaultBranding.homepageProductNameBackground || "",
                homepage_product_heading:
                  defaultBranding.productHeadingText || "",
              };
              swagStoreStore.saveThemeDetails(defaultThemeData).catch(() => {
                // Silently handle errors - don't show error messages
              });
            })
            .catch((error) => {
              showError(
                "Error",
                error?.response?.data?.message ||
                  "Failed to create store. Please try again.",
              );
            });
          break;
        }

        case SWAGSTORE_STEPS.BRANDING: {
          if (!swagStoreStore.storeId || !swagStoreStore.themeId) {
            showError(
              "Error",
              "Store ID and Theme ID are required. Please create a store first.",
            );
            return;
          }

          const getFileOrFirst = (
            file: File | (File | string)[] | null,
          ): File | null => {
            if (!file) return null;
            if (Array.isArray(file)) {
              const firstFile = file.find(
                (item): item is File => item instanceof File,
              );
              return firstFile || null;
            }
            return file instanceof File ? file : null;
          };

          const getFileArray = (
            file: File | (File | string)[] | null,
          ): File[] => {
            if (!file) return [];
            if (Array.isArray(file)) {
              return file.filter((item): item is File => item instanceof File);
            }
            return file instanceof File ? [file] : [];
          };

          const getFirstExistingUrl = (
            file: (File | string)[] | null,
          ): string | null => {
            if (!file) return null;
            const existingUrl = file.find(
              (item): item is string => typeof item === "string",
            );
            return existingUrl || null;
          };

          const uploadFilesAndBuildThemeData = async () => {
            const imageAPI = new ImageAPI();
            const themeData: Partial<ThemeRequestBody> = {
              store_id: swagStoreStore.storeId!,
              theme_id: swagStoreStore.themeId!,
            };
            try {
              // Upload favicon if it's a File
              const faviconFile = getFileOrFirst(branding.favicon);
              if (faviconFile instanceof File) {
                const urls = await imageAPI.uploadImage(
                  [faviconFile],
                  "swagstore-images",
                );
                if (urls.length > 0) {
                  themeData.favicon_url = urls[0];
                } else {
                  // If upload fails or no URL, set to empty string to clear it
                  themeData.favicon_url = "";
                }
              } else {
                const faviconUrl = getFirstExistingUrl(branding.favicon);
                // Always include favicon_url (even if empty) so deletions are persisted
                themeData.favicon_url = faviconUrl || "";
              }

              // Upload logo if it's a File
              const logoFile = getFileOrFirst(branding.logo);
              if (logoFile instanceof File) {
                const urls = await imageAPI.uploadImage(
                  [logoFile],
                  "swagstore-images",
                );
                if (urls.length > 0) {
                  themeData.company_logo = urls[0];
                } else {
                  // If upload fails or no URL, set to empty string to clear it
                  themeData.company_logo = "";
                }
              } else {
                const logoUrl = getFirstExistingUrl(branding.logo);
                // Always include company_logo (even if empty) so deletions are persisted
                themeData.company_logo = logoUrl || "";
              }
              // Upload carousel images if they are Files
              const carouselFiles = getFileArray(branding.carouselImages);
              const carouselUrls: string[] = [];

              // First, add existing string URLs (already saved images)
              if (
                branding.carouselImages &&
                Array.isArray(branding.carouselImages)
              ) {
                for (const item of branding.carouselImages) {
                  if (typeof item === "string") {
                    carouselUrls.push(item);
                  }
                }
              }

              // Then, upload new files and add their URLs
              if (carouselFiles.length > 0) {
                const uploadedCarouselUrls = await imageAPI.uploadImage(
                  carouselFiles,
                  "swagstore-images",
                );
                carouselUrls.push(...uploadedCarouselUrls);
              }

              // Set carousel images as company_banners
              // Always include company_banners (even if empty) so deletions are persisted
              themeData.company_banners = carouselUrls;

              // Map color fields - always include all fields (even if empty) so deletions are persisted
              themeData.app_button_color = branding.primaryColor || "";
              themeData.app_hover_button_color = branding.secondaryColor || "";
              themeData.app_button_secondary_color =
                branding.secondaryColor || "";
              themeData.app_button_secondary_hover_color =
                branding.secondaryColor || "";
              themeData.store_background_color =
                branding.storeBackgroundColor || "";
              themeData.active_textfield_color =
                branding.textFieldActiveColor || "";
              themeData.disabled_textfield_color =
                branding.textFieldDisabledColor || "";
              themeData.homepage_product_card_background =
                branding.homepageProductCardBackground || "";
              themeData.homepage_product_name_background =
                branding.homepageProductNameBackground || "";
              themeData.homepage_product_heading =
                branding.productHeadingText || "";
              themeData.footer_text = branding.footerText || "";
              themeData.meta_title = branding.metaTitle || "";
              themeData.meta_description = branding.metaDescription || "";
              // Save theme details
              swagStoreStore
                .saveThemeDetails(themeData)
                .then(() => {
                  showSuccess("Branding settings saved successfully");
                })
                .catch((error: any) => {
                  showError(
                    "Error",
                    error?.response?.data?.message ||
                      "Failed to update branding settings. Please try again.",
                  );
                });
            } catch (error: any) {
              showError(
                "Error",
                error?.response?.data?.message ||
                  "Failed to update branding settings. Please try again.",
              );
            }
          };

          uploadFilesAndBuildThemeData();
          break;
        }

        case SWAGSTORE_STEPS.LOGIN: {
          if (!swagStoreStore.storeId || !swagStoreStore.themeId) {
            showError(
              "Error",
              "Store ID and Theme ID are required. Please create a store first.",
            );
            return;
          }

          // Upload login page image if it's a File
          const uploadLoginImageAndSave = async () => {
            const imageAPI = new ImageAPI();
            const themeData: Partial<ThemeRequestBody> = {
              store_id: swagStoreStore.storeId!,
              theme_id: swagStoreStore.themeId!,
            };

            try {
              if (loginPage.loginPageImage instanceof File) {
                const urls = await imageAPI.uploadImage(
                  [loginPage.loginPageImage],
                  "swagstore-images",
                );
                if (urls.length > 0) {
                  themeData.login_page_image_url = urls[0];
                } else {
                  // If upload fails or no URL, set to empty string to clear it
                  themeData.login_page_image_url = "";
                }
              } else if (typeof loginPage.loginPageImage === "string") {
                themeData.login_page_image_url = loginPage.loginPageImage;
              } else {
                // Always include login_page_image_url (even if empty) so deletions are persisted
                themeData.login_page_image_url = "";
              }

              // Map login page text fields - always include all fields (even if empty) so deletions are persisted
              themeData.login_page_heading_text = loginPage.headingText || "";
              themeData.login_page_subheading_text =
                loginPage.subheadingText || "";

              // Save theme details
              swagStoreStore
                .saveThemeDetails(themeData)
                .then(() => {
                  showSuccess("Login page settings saved successfully");
                })
                .catch((error: any) => {
                  showError(
                    "Error",
                    error?.response?.data?.message ||
                      "Failed to update login page settings. Please try again.",
                  );
                });
            } catch (error: any) {
              showError(
                "Error",
                error?.response?.data?.message ||
                  "Failed to update login page settings. Please try again.",
              );
            }
          };

          uploadLoginImageAndSave();
          break;
        }

        case SWAGSTORE_STEPS.PRODUCT_MAPPING: {
          if (
            !userStore.hasPermission(
              EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
            )
          ) {
            showError(
              "Error",
              "You don't have permission to edit products in swagstore.",
            );
            return;
          }

          // Convert productPrices to array format
          const products = Object.entries(productMapping.productPrices).map(
            ([productId, sellingPrice]) => ({
              product_id: parseInt(productId),
              selling_price: sellingPrice ?? 0,
            }),
          );

          const requestBody: ProductMappingRequestBody = {
            store_id: swagStoreStore.storeId || undefined,
            products: products,
            product_prices: productMapping.productPrices,
          };

          if (!swagStoreStore.storeId) {
            showError(
              "Error",
              "Store ID is required. Please create a store first.",
            );
            return;
          }

          swagStoreStore
            .updateProductMappingAPI(requestBody)
            .then((response: any) => {
              showSuccess("Product mapping saved successfully");
            })
            .catch((error: any) => {
              showError(
                "Error",
                error?.response?.data?.message ||
                  "Failed to update product mapping settings. Please try again.",
              );
            });
          break;
        }

        case SWAGSTORE_STEPS.MOCKUPS: {
          showSuccess(
            "Use Generate mockup in the center preview — mockup files are not part of theme save.",
          );
          break;
        }

        default:
          showError("Error", "Invalid step selected");
          return;
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          `Failed to save ${selectedItem} settings. Please try again.`,
      );
    }
  };

  // Product Settings Handlers
  const handleProductPriceChange = (
    productId: number,
    price: number | null,
  ) => {
    const updatedPrices = {
      ...swagStoreStore.settings.productMapping.productPrices,
      [productId]: price,
    };
    swagStoreStore.updateProductMapping({ productPrices: updatedPrices });
  };

  const refreshProductPricesAfterPricingChange = async () => {
    return await Promise.all([
      swagStoreStore.getPacks(),
      swagStoreStore.getProducts(),
    ]).then(() => {
      swagStoreStore.setProductsToDisplayOnProductPage();
      swagStoreStore.setBaseProductsToDisplay();

      const updatedProductPrices: Record<number, number | null> = {};
      swagStoreStore.baseProductsToDisplay.forEach((product) => {
        if (product.pack_price !== undefined && product.pack_price !== null) {
          updatedProductPrices[product.id] = product.pack_price;
        }
      });

      swagStoreStore.updateProductMapping({
        productPrices: updatedProductPrices,
      });
    });
  };

  const handleBulkPricingApply = () => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Store ID is required. Please create a store first.");
      return;
    }

    const markupPercentage =
      swagStoreStore.settings.productMapping.markupPercentage;
    swagStoreStore.swagStoreAPI
      .applyMarkupPercentage(swagStoreStore.storeId, markupPercentage)
      .then((response) => {
        if (response) {
          showSuccess(
            response.message ||
              `Markup percentage of ${markupPercentage}% applied successfully to all products and packs`,
          );

          refreshProductPricesAfterPricingChange().catch((err) => {
            console.error("Error refreshing products after markup:", err);
          });
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to apply markup percentage. Please try again.",
        );
      });
  };

  const handleResetPricing = () => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Store ID is required. Please create a store first.");
      return;
    }

    swagStoreStore.swagStoreAPI
      .resetPricing(swagStoreStore.storeId)
      .then((response) => {
        if (response) {
          showSuccess(
            response.message ||
              "Prices reset successfully for all products and packs",
          );
          refreshProductPricesAfterPricingChange().catch((err) => {
            console.error("Error refreshing products after price reset:", err);
          });
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reset prices. Please try again.",
        );
      });
  };

  /*
  const handleBulkDiscountApply = () => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Store ID is required. Please create a store first.");
      return;
    }

    const discountPercentage =
      swagStoreStore.settings.productMapping.discountPercentage;
    swagStoreStore.swagStoreAPI
      .applyDiscountPercentage(swagStoreStore.storeId, discountPercentage)
      .then((response) => {
        if (response) {
          showSuccess(
            response.message ||
              `Discount percentage of ${discountPercentage}% applied successfully to all products and packs`,
          );

          refreshProductPricesAfterPricingChange().catch((err) => {
            console.error("Error refreshing products after discount:", err);
          });
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to apply discount percentage. Please try again.",
        );
      });
  };
  */

  const handleDownloadProductsPacksCSV = () => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Store ID is required. Please create a store first.");
      return;
    }
    swagStoreStore
      .downloadProductsPacksCSV()
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename ? response.filename : `Products.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          showError(
            "Error",
            "Failed to download the Catalogue template csv file.",
          );
        }
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message || "Failed to download the CSV file.",
        );
      });
  };

  const handleUploadProductsPacksCSV = (file: File) => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Store ID is required. Please create a store first.");
      return;
    }
    swagStoreStore
      .uploadProductsPacksCSV(file)
      .then((response) => {
        if (response) {
          showSuccess(response.message || "CSV file uploaded successfully");
          Promise.all([swagStoreStore.getPacks(), swagStoreStore.getProducts()])
            .then(() => {
              swagStoreStore.setProductsToDisplayOnProductPage();
            })
            .catch((err) => {
              console.error("Error refreshing products after CSV upload:", err);
              showError(
                "Error",
                "Failed to refresh products after upload. Please reload the page.",
              );
            });
        }
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Failed to upload CSV file. Please try again.",
        );
      });
  };

  const handleAddProducts = (csvFile?: File | null) => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Store ID is required. Please create a store first.");
      return;
    }

    // Handle CSV upload
    if (csvFile) {
      swagStoreStore
        .uploadProductsCSV(csvFile)
        .then((response) => {
          if (response) {
            showSuccess(response.message || "CSV file uploaded successfully");
            // After successful upload, refresh both packs and products
            Promise.all([
              swagStoreStore.getPacks(),
              swagStoreStore.getProducts(),
            ])
              .then(() => {
                swagStoreStore.setProductsToDisplayOnProductPage();
              })
              .catch((err) => {
                console.error(
                  "Error refreshing products after CSV upload:",
                  err,
                );
                showError(
                  "Error",
                  "Failed to refresh products after upload. Please reload the page.",
                );
              });
          }
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Failed to upload CSV file. Please try again.",
          );
        });
      return;
    }

    const promises: Promise<any>[] = [];
    const isInventory = swagStoreStore.selectedSource === "inventory";

    let packsDelta: Array<{
      pack_id: number;
      pack_price?: number;
      display_on_store?: boolean;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }> = [];

    let productsDelta: Array<{
      product_id: number;
      selling_price?: number;
      display_on_store?: boolean;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }> = [];

    if (isInventory) {
      packsDelta = swagStoreStore.calculatePacksDelta();
      if (packsDelta.length > 0) {
        promises.push(
          swagStoreStore.swagStoreAPI
            .togglePacksDisplay(swagStoreStore.storeId, packsDelta)
            .then((response) => {
              if (response) {
                const added = packsDelta
                  .filter((p) => p.display_on_store === true)
                  .map((p) => p.pack_id);
                const removed = packsDelta
                  .filter((p) => p.display_on_store === false)
                  .map((p) => p.pack_id);
                swagStoreStore.updatePacksDisplayStatus(added, removed);
              }
            }),
        );
      }
    } else {
      // Calculate delta for catalogue products only
      productsDelta = swagStoreStore.calculateProductsDelta();
      if (productsDelta.length > 0) {
        promises.push(
          swagStoreStore.swagStoreAPI
            .toggleProductsDisplay(swagStoreStore.storeId, productsDelta)
            .then((response) => {
              if (response) {
                const added = productsDelta
                  .filter((p) => p.display_on_store === true)
                  .map((p) => p.product_id);
                const removed = productsDelta
                  .filter((p) => p.display_on_store === false)
                  .map((p) => p.product_id);
                swagStoreStore.updateProductsDisplayStatus(added, removed);
              }
            }),
        );
      }
    }

    if (promises.length === 0) {
      showError("Error", "No changes to save.");
      return;
    }

    Promise.all(promises)
      .then(() => {
        // Update variant quantities locally (backend adds the value)
        if (isInventory && packsDelta.length > 0) {
          swagStoreStore.updatePackVariantQuantitiesLocally(packsDelta);
        } else if (!isInventory && productsDelta.length > 0) {
          swagStoreStore.updateProductVariantQuantitiesLocally(productsDelta);
          swagStoreStore.updateProductsLimitedStockLocally(productsDelta);
        }

        // Clear edited variant quantities so inputs show the new calculated values
        swagStoreStore.clearEditedQuantities();

        // Update productPrices locally with the saved prices
        const updatedProductPrices = {
          ...swagStoreStore.settings.productMapping.productPrices,
        };

        let hasPriceUpdates = false;

        // Update prices from packs (inventory)
        if (packsDelta && packsDelta.length > 0) {
          packsDelta.forEach((pack) => {
            if (pack.pack_price !== undefined) {
              updatedProductPrices[pack.pack_id] = pack.pack_price;
              hasPriceUpdates = true;
            }
          });
        }

        // Update prices from products (catalogue)
        if (productsDelta && productsDelta.length > 0) {
          productsDelta.forEach((product) => {
            if (product.selling_price !== undefined) {
              updatedProductPrices[product.product_id] = product.selling_price;
              hasPriceUpdates = true;
            }
          });
        }

        // Update settings with the new prices
        if (hasPriceUpdates) {
          swagStoreStore.updateProductMapping({
            productPrices: updatedProductPrices,
          });
        }

        const packsAdded = packsDelta
          ? packsDelta.filter((p) => p.display_on_store === true).length
          : 0;
        const packsRemoved = packsDelta
          ? packsDelta.filter((p) => p.display_on_store === false).length
          : 0;
        const productsAdded = productsDelta
          ? productsDelta.filter((p) => p.display_on_store === true).length
          : 0;
        const productsRemoved = productsDelta
          ? productsDelta.filter((p) => p.display_on_store === false).length
          : 0;
        const totalChanges =
          packsAdded + packsRemoved + productsAdded + productsRemoved;
        if (totalChanges > 0) {
          const addedCount = packsAdded + productsAdded;
          const removedCount = packsRemoved + productsRemoved;
          let message = "";
          if (addedCount > 0 && removedCount > 0) {
            message = `${addedCount} product${
              addedCount !== 1 ? "s" : ""
            } added and ${removedCount} product${
              removedCount !== 1 ? "s" : ""
            } removed successfully`;
          } else if (addedCount > 0) {
            message = `${addedCount} product${
              addedCount !== 1 ? "s" : ""
            } added to store successfully`;
          } else if (removedCount > 0) {
            message = `${removedCount} product${
              removedCount !== 1 ? "s" : ""
            } removed from store successfully`;
          }
          showSuccess(message);
        } else {
          showSuccess("Updated successfully.");
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to update products. Please try again.",
        );
      });
  };

  const handlePreviewStore = () => {
    if (!swagStoreStore.storeId) {
      showError("Error", "Please create a store first before previewing.");
      return;
    }

    if (!swagStoreStore.themeId) {
      showError("Error", "Theme ID is required. Please create a store first.");
      return;
    }

    // Include the current selected item in the URL so the preview opens the correct page
    const selectedItem =
      swagStoreStore.selectedItem || SWAGSTORE_STEPS.PRODUCT_MAPPING;
    const previewUrl = `/swagstore/preview?store_id=${swagStoreStore.storeId}&theme_id=${swagStoreStore.themeId}&step=${selectedItem}`;
    // Open in new tab instead of new window
    const newTab = window.open(previewUrl, "_blank");
    if (newTab) {
      newTab.opener = null; // Security: prevent the new tab from accessing window.opener
    }
  };

  const handlePublishStore = () => {
    // TODO: Implement publish store functionality
    console.log("Publish store clicked");
  };

  const handleExit = () => {
    // TODO: Implement exit functionality or use navigate
    console.log("Exit clicked");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
        width: "100%",
        maxWidth: "100vw",
        position: "relative",
      }}
    >
      {/* Common Header - Always visible */}
      <SwagstoreHeader
        onPreviewStore={handlePreviewStore}
        onPublishStore={handlePublishStore}
        onExit={handleExit}
        lastSavedTime="just now"
      />

      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: { xs: "column", lg: "row" },
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* Mobile Menu Buttons */}
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              zIndex: 1000,
            }}
          >
            <IconButton
              onClick={() => swagStoreStore.setLeftDrawerOpen(true)}
              sx={{ color: theme.palette.primary.main }}
            >
              <MenuIcon />
            </IconButton>
            <IconButton
              onClick={() => swagStoreStore.setRightDrawerOpen(true)}
              sx={{ color: theme.palette.primary.main }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        )}

        {/* Left Sidebar - Hidden on mobile, visible on large screens */}
        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "300px",
            flexShrink: 0,
          }}
        >
          <LeftSidebar
            selectedItem={swagStoreStore.selectedItem}
            onItemSelect={handleItemSelect}
            storeId={swagStoreStore.storeId}
          />
        </Box>

        {/* Mobile Left Drawer */}
        <Drawer
          anchor="left"
          open={swagStoreStore.leftDrawerOpen}
          onClose={() => swagStoreStore.setLeftDrawerOpen(false)}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              width: "320px",
              boxSizing: "border-box",
            },
          }}
        >
          <LeftSidebar
            selectedItem={swagStoreStore.selectedItem}
            onItemSelect={handleItemSelect}
            storeId={swagStoreStore.storeId}
          />
        </Drawer>

        {/* Content Area with Preview and Right Sidebar */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            minWidth: 0,
            overflow: { xs: "auto", lg: "hidden" },
            overflowY: { xs: "auto", lg: "hidden" },
          }}
        >
          {/* Store Preview - Takes full width on mobile, flex on desktop */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: { xs: "auto", lg: "100vh" },
              overflow: { xs: "auto", lg: "hidden" },
              overflowY: { xs: "auto", lg: "hidden" },
            }}
          >
            <StorePreview
              settings={swagStoreStore.settings}
              selectedItem={swagStoreStore.selectedItem}
              deviceView={swagStoreStore.deviceView}
              onDeviceViewChange={swagStoreStore.setDeviceView}
              previewWidth={getPreviewWidth()}
              loginPageImageUrl={getLoginPageImageUrl()}
            />
          </Box>

          {/* Right Sidebar - Hidden on mobile, visible on large screens */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              width: "335px",
              flexShrink: 0,
            }}
          >
            <RightSidebar
              selectedItem={swagStoreStore.selectedItem}
              settings={swagStoreStore.settings}
              customers={customerStore?.customers || []}
              storeDetailsErrors={swagStoreStore.storeDetailsErrors}
              onSettingChange={handleThemeCHange}
              onSaveSettings={handleSaveSettings}
              onProductPriceChange={handleProductPriceChange}
              onBulkPricingApply={handleBulkPricingApply}
              onAddProducts={handleAddProducts}
              onDownloadProductsPacksCSV={handleDownloadProductsPacksCSV}
              onUploadProductsPacksCSV={handleUploadProductsPacksCSV}
              onSearchTermChange={swagStoreStore.setProductSearchTerm}
              onCategoryChange={swagStoreStore.setSelectedCategory}
              onProductIdChange={swagStoreStore.setSelectedProductId}
              onBulkUpdateToggle={(enabled) =>
                swagStoreStore.updateProductMapping({
                  bulkUpdateEnabled: enabled,
                })
              }
              onMarkupPercentageChange={(percentage) =>
                swagStoreStore.updateProductMapping({
                  markupPercentage: percentage,
                })
              }
              onDiscountUpdateToggle={(enabled) =>
                swagStoreStore.updateProductMapping({
                  discountUpdateEnabled: enabled,
                })
              }
              onDiscountPercentageChange={(percentage) =>
                swagStoreStore.updateProductMapping({
                  discountPercentage: percentage,
                })
              }
              onBulkDiscountApply={() => {
                /* no-op: handleBulkDiscountApply is commented out above; enable bulk discount UI then uncomment handler */
              }}
              onResetPricing={handleResetPricing}
              onModalOpenChange={swagStoreStore.setModalOpen}
            />
          </Box>
        </Box>

        {/* Mobile Right Drawer */}
        <Drawer
          anchor="right"
          open={swagStoreStore.rightDrawerOpen}
          onClose={() => swagStoreStore.setRightDrawerOpen(false)}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              width: "320px",
              boxSizing: "border-box",
            },
          }}
        >
          <RightSidebar
            selectedItem={swagStoreStore.selectedItem}
            settings={swagStoreStore.settings}
            customers={customerStore?.customers || []}
            storeDetailsErrors={swagStoreStore.storeDetailsErrors}
            onSettingChange={handleThemeCHange}
            onSaveSettings={handleSaveSettings}
            onProductPriceChange={handleProductPriceChange}
            onBulkPricingApply={handleBulkPricingApply}
            onAddProducts={handleAddProducts}
            onDownloadProductsPacksCSV={handleDownloadProductsPacksCSV}
            onUploadProductsPacksCSV={handleUploadProductsPacksCSV}
            onSearchTermChange={swagStoreStore.setProductSearchTerm}
            onCategoryChange={swagStoreStore.setSelectedCategory}
            onProductIdChange={swagStoreStore.setSelectedProductId}
            onBulkUpdateToggle={(enabled) =>
              swagStoreStore.updateProductMapping({
                bulkUpdateEnabled: enabled,
              })
            }
            onMarkupPercentageChange={(percentage) =>
              swagStoreStore.updateProductMapping({
                markupPercentage: percentage,
              })
            }
            onDiscountUpdateToggle={(enabled) =>
              swagStoreStore.updateProductMapping({
                discountUpdateEnabled: enabled,
              })
            }
            onDiscountPercentageChange={(percentage) =>
              swagStoreStore.updateProductMapping({
                discountPercentage: percentage,
              })
            }
            onBulkDiscountApply={() => {
              /* no-op: handleBulkDiscountApply is commented out above; enable bulk discount UI then uncomment handler */
            }}
            onResetPricing={handleResetPricing}
            onModalOpenChange={swagStoreStore.setModalOpen}
          />
        </Drawer>
      </Box>
    </Box>
  );
});

export default Swagstore;
